import { useEffect, useState } from 'react';
import styled from '@/styles';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import ScoreRing from './ScoreRing';
import TierPill from './TierPill';
import TierPopover from './TierPopover';
import StatusBadgeRow from './StatusBadgeRow';
import StatCards from './StatCards';
import ActivityFeed from './ActivityFeed';
import ScoreHistoryModal from './ScoreHistoryModal';
import RecentActivityModal from './RecentActivityModal';
import useDependabilityEvents from './useDependabilityEvents';

// Deliberately NOT elements/Card: the 2.0-Baseline card style is flat and
// outlined, which would mean negating Card's shadow and radius tokens.
const Wrapper = styled('div', {
  width: '100%',
  backgroundColor: 'white',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  borderRadius: '12px',
  border: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const TopSection = styled('div', {
  display: 'flex',
  gap: '32px',
  // ring column is vertically centered against the taller right column
  alignItems: 'center',
  flexWrap: 'wrap',
  paddingBottom: '24px',
  borderBottom: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const ScoreColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});
const RightColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: '1 1 320px',
  minWidth: 0,
});
const BadgePillRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});
const Breakdown = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'center',
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$sm',
  textDecoration: 'underline',
});

const FEED_PAGE_SIZE = 3;

interface Props {
  workerId: number;
  data: DependabilityV2;
}

const DependabilityV2Card = ({ workerId, data }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  // The inline feed shows only the first page; "View more" opens the
  // activity modal (view-activities frame) rather than growing the card.
  const { events, loading, loaded, error, loadPage, hasMore } =
    useDependabilityEvents(workerId, FEED_PAGE_SIZE);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  return (
    <Wrapper data-testid="dependability-v2-card">
      <TopSection>
        <ScoreColumn>
          <ScoreRing score={data.score} tier={data.tier} />
          <Breakdown type="button" onClick={() => setHistoryOpen(true)}>
            See breakdown
          </Breakdown>
        </ScoreColumn>
        <RightColumn>
          <BadgePillRow>
            <StatusBadgeRow badges={data.badges} />
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <TierPill
                tier={data.tier}
                label={data.tier_label}
                onInfoClick={() => setPopoverOpen(true)}
                expanded={popoverOpen}
              />
              <TierPopover
                isOpen={popoverOpen}
                onRequestClose={() => setPopoverOpen(false)}
                // the pill anchors at the card's right edge, so the popover
                // must extend leftward or it clips offscreen
                justify="right"
                boundaries={data.tier_boundaries}
                score={data.score}
                tier={data.tier}
                completedShifts={data.completed_shifts_count}
                scoringMode={data.scoring_mode}
              />
            </span>
          </BadgePillRow>
          <StatCards stats={data.stats} />
        </RightColumn>
      </TopSection>
      <ActivityFeed
        events={events}
        hasMore={hasMore}
        loading={loading || !loaded}
        error={error}
        onRetry={() => loadPage(1)}
        onViewMore={() => setActivityOpen(true)}
      />
      <RecentActivityModal
        workerId={workerId}
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
      />
      <ScoreHistoryModal
        workerId={workerId}
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        signals={data.signals}
        completedShifts={data.completed_shifts_count}
        scoringMode={data.scoring_mode}
      />
    </Wrapper>
  );
};

export default DependabilityV2Card;
